import type { ButtonDesignTokens, ButtonTokenSections } from '@primeuix/themes/types/button';

export const root: ButtonTokenSections.Root = {
    // ===== ROOT BUTTON CONFIGURATION ===== (DS button — radius-medium, theme label weight, semantic focus ring)
    // Basic layout and spacing
    borderRadius: '{borderRadius.md}',
    roundedBorderRadius: '{borderRadius.md}',   // O5: no pill buttons — [rounded] renders radius-medium
    gap: '{spacing.small.2}',
    paddingX: '{extend.buttonpaddinghorizontal}',
    paddingY: '{extend.buttonpaddingvertical}',
    iconOnlyWidth: '{extend.buttonheight}',

    // Size variants
    sm: {
        fontSize: '0.8125rem',
        paddingX: '{spacing.medium.2}',
        paddingY: '{spacing.small.3}',
        iconOnlyWidth: '{extend.buttonheight}',
    },
    lg: {
        fontSize: '1.0625rem',
        paddingX: '{spacing.medium.3}',
        paddingY: '{spacing.medium.1}',
        iconOnlyWidth: '{extend.buttonheightlarge}',
    },

    // Typography
    label: {
        fontWeight: '{extend.buttonfontweight}',
    },

    // Effects and interaction
    raisedShadow: '{extend.shadowsm}',   // guide: burgundy shadow-small, never black
    transitionDuration: '{extend.transitionDuration.fast}',

    // Focus ring: the theme's single semantic rule (DS: 2px wine, 2px offset)
    focusRing: {
        width: '{focusRing.width}',
        style: '{focusRing.style}',
        offset: '{focusRing.offset}',
    },

    // Badge styling for notification badges on buttons
    badgeSize: '1rem',
};

export const colorScheme: ButtonTokenSections.ColorScheme = {
    // Brand guide button (decision log O1/O4/O14/O17). Only `primary`, `secondary`
    // and `link` exist in the guide; the other PrimeNG severities map onto them
    // or onto the feedback roles so no severity can produce off-brand colour or
    // sub-AA text. Hover/active never change colour — depth changes instead
    // (lift + shadow-small, see cleverly-overrides.scss).
    light: {
        root: {
            // ===== PRIMARY: orange surface, wine label (4.75:1) =====
            primary: {
                background: '{primary.500}',
                hoverBackground: '{primary.500}',
                activeBackground: '{primary.500}',
                borderColor: '{primary.500}',
                hoverBorderColor: '{primary.500}',
                activeBorderColor: '{primary.500}',
                color: '{primary.contrastColor}',
                hoverColor: '{primary.contrastColor}',
                activeColor: '{primary.contrastColor}',
                focusRing: { color: '{focusRing.color}', shadow: 'none' },
            },

            // ===== SECONDARY (O14, surface family 2026-10-09): white surface, neutral-lighter
            // hairline, resting shadow-small (cleverly-overrides.scss), neutral-darkest label =====
            secondary: {
                background: '{extend.buttonsurface}',
                hoverBackground: '{extend.buttonsurface}',
                activeBackground: '{extend.buttonsurface}',
                borderColor: '{extend.buttonsurfaceborder}',
                hoverBorderColor: '{extend.buttonsurfaceborder}',
                activeBorderColor: '{extend.buttonsurfaceborder}',
                color: '{extend.textneutral}',
                hoverColor: '{extend.textneutral}',
                activeColor: '{extend.textneutral}',
                focusRing: { color: '{focusRing.color}', shadow: 'none' },
            },

            // ===== SUCCESS: no green in product UI; an affirmative action is the primary =====
            success: {
                background: '{primary.500}',
                hoverBackground: '{primary.500}',
                activeBackground: '{primary.500}',
                borderColor: '{primary.500}',
                hoverBorderColor: '{primary.500}',
                activeBorderColor: '{primary.500}',
                color: '{primary.contrastColor}',
                hoverColor: '{primary.contrastColor}',
                activeColor: '{primary.contrastColor}',
                focusRing: { color: '{focusRing.color}', shadow: 'none' },
            },

            // ===== INFO = the ACCENT button (2026-10-09): Knowledge Blue surface, wine
            // label (9.9:1) — more weight than a surface button, still not the one orange
            // CTA. Use for the main non-CTA action of a page ("Add New Appointment"). =====
            info: {
                background: '{extend.surfaceaccent}',
                hoverBackground: '{extend.surfaceaccent}',
                activeBackground: '{extend.surfaceaccent}',
                borderColor: '{extend.surfaceaccent}',
                hoverBorderColor: '{extend.surfaceaccent}',
                activeBorderColor: '{extend.surfaceaccent}',
                color: '{extend.textheading}',
                hoverColor: '{extend.textheading}',
                activeColor: '{extend.textheading}',
                focusRing: { color: '{focusRing.color}', shadow: 'none' },
            },
            // ===== HELP: no guide role (purple is Complori) → secondary look =====
            help: {
                background: '{extend.buttonsurface}',
                hoverBackground: '{extend.buttonsurface}',
                activeBackground: '{extend.buttonsurface}',
                borderColor: '{extend.buttonsurfaceborder}',
                hoverBorderColor: '{extend.buttonsurfaceborder}',
                activeBorderColor: '{extend.buttonsurfaceborder}',
                color: '{extend.textneutral}',
                hoverColor: '{extend.textneutral}',
                activeColor: '{extend.textneutral}',
                focusRing: { color: '{focusRing.color}', shadow: 'none' },
            },

            // ===== WARN: the guide's cream accent surface, wine label =====
            warn: {
                background: '{extend.surfacecream}',
                hoverBackground: '{extend.surfacecream}',
                activeBackground: '{extend.surfacecream}',
                borderColor: '{extend.surfacecream}',
                hoverBorderColor: '{extend.surfacecream}',
                activeBorderColor: '{extend.surfacecream}',
                color: '{extend.textheading}',
                hoverColor: '{extend.textheading}',
                activeColor: '{extend.textheading}',
                focusRing: { color: '{focusRing.color}', shadow: 'none' },
            },

            // ===== DANGER: destructive action, wine surface, white label (13.6:1) =====
            danger: {
                background: '{wine.900}',
                hoverBackground: '{wine.900}',
                activeBackground: '{wine.900}',
                borderColor: '{wine.900}',
                hoverBorderColor: '{wine.900}',
                activeBorderColor: '{wine.900}',
                color: '#FFFFFF',
                hoverColor: '#FFFFFF',
                activeColor: '#FFFFFF',
                focusRing: { color: '{focusRing.color}', shadow: 'none' },
            },

            // ===== CONTRAST: the guide's `alternate` primary (neutral-darkest) =====
            contrast: {
                background: '{sand.950}',
                hoverBackground: '{sand.950}',
                activeBackground: '{sand.950}',
                borderColor: '{sand.950}',
                hoverBorderColor: '{sand.950}',
                activeBorderColor: '{sand.950}',
                color: '#FFFFFF',
                hoverColor: '#FFFFFF',
                activeColor: '#FFFFFF',
                focusRing: { color: '{focusRing.color}', shadow: 'none' },
            },
        },

        // ===== OUTLINED (O17): no guide variant, so every outlined button renders as
        // the guide secondary (transparent, 1.5px neutral border); the label keeps a
        // readable severity colour. =====
        outlined: {
            primary: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.textneutral}' },
            secondary: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.textneutral}' },
            success: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.statesuccess}' },
            info: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.textheading}' },
            warn: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.textheading}' },
            help: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.textneutral}' },
            danger: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.textheading}' },
            contrast: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.textneutral}' },
            plain: { hoverBackground: '{extend.buttonsurface}', activeBackground: '{extend.buttonsurface}', borderColor: '{extend.buttonsurfaceborder}', color: '{extend.textneutral}' },
        },

        // ===== TEXT buttons (icon/toolbar buttons): body text colour like the rest of
        // the UI (O2); status severities keep their feedback colour; neutral 5% hover
        // wash instead of orange/green/purple tints. =====
        text: {
            primary: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{text.color}' },
            secondary: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{text.color}' },
            success: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{extend.statesuccess}' },
            info: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{extend.textheading}' },
            warn: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{extend.textheading}' },
            help: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{text.color}' },
            danger: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{extend.textheading}' },
            contrast: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{text.color}' },
            plain: { hoverBackground: '{extend.surfacesubtle}', activeBackground: '{extend.surfacesubtle}', color: '{text.color}' },
        },

        // ===== LINK =====
        link: {
            color: '{extend.buttonlinkcolor}',
            hoverColor: '{extend.buttonlinkhovercolor}',
            activeColor: '{extend.buttonlinkhovercolor}',
        },
    },
    dark: {
        root: {
            // ===== PRIMARY BUTTON (SAME AS LIGHT) =====
            primary: {
                background: '{primary.500}',
                hoverBackground: '{primary.400}',
                activeBackground: '{primary.600}',
                borderColor: '{primary.500}',
                hoverBorderColor: '{primary.400}',
                activeBorderColor: '{primary.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{primary.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {primary.500}, transparent 70%)',
                },
            },

            // ===== SECONDARY BUTTON (SAME AS LIGHT) =====
            secondary: {
                background: '{surface.800}',
                hoverBackground: '{surface.700}',
                activeBackground: '{surface.900}',
                borderColor: '{surface.600}',
                hoverBorderColor: '{surface.500}',
                activeBorderColor: '{surface.700}',
                color: '{surface.200}',
                hoverColor: '{surface.100}',
                activeColor: '{surface.200}',
                focusRing: {
                    color: '{surface.400}',
                    shadow: '0 0 0 2px color-mix(in srgb, {surface.400}, transparent 70%)',
                },
            },

            // ===== SUCCESS BUTTON (GREEN) =====
            success: {
                background: '{green.500}',
                hoverBackground: '{green.400}',
                activeBackground: '{green.600}',
                borderColor: '{green.500}',
                hoverBorderColor: '{green.400}',
                activeBorderColor: '{green.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{green.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {green.500}, transparent 70%)',
                },
            },

            // ===== INFO BUTTON (BLUE - SAME AS PRIMARY) =====
            info: {
                background: '{primary.500}',
                hoverBackground: '{primary.400}',
                activeBackground: '{primary.600}',
                borderColor: '{primary.500}',
                hoverBorderColor: '{primary.400}',
                activeBorderColor: '{primary.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{primary.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {primary.500}, transparent 70%)',
                },
            },

            // ===== WARN BUTTON (YELLOW/ORANGE) =====
            warn: {
                background: '{yellow.500}',
                hoverBackground: '{yellow.400}',
                activeBackground: '{yellow.600}',
                borderColor: '{yellow.500}',
                hoverBorderColor: '{yellow.400}',
                activeBorderColor: '{yellow.600}',
                color: '{surface.900}',
                hoverColor: '{surface.900}',
                activeColor: '{surface.900}',
                focusRing: {
                    color: '{yellow.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {yellow.500}, transparent 70%)',
                },
            },

            // ===== HELP BUTTON (PURPLE) =====
            help: {
                background: '{purple.500}',
                hoverBackground: '{purple.400}',
                activeBackground: '{purple.600}',
                borderColor: '{purple.500}',
                hoverBorderColor: '{purple.400}',
                activeBorderColor: '{purple.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{purple.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {purple.500}, transparent 70%)',
                },
            },

            // ===== DANGER BUTTON (RED) =====
            danger: {
                background: '{red.500}',
                hoverBackground: '{red.400}',
                activeBackground: '{red.600}',
                borderColor: '{red.500}',
                hoverBorderColor: '{red.400}',
                activeBorderColor: '{red.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{red.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {red.500}, transparent 70%)',
                },
            },

            // ===== CONTRAST BUTTON (LIGHT) =====
            contrast: {
                background: '{surface.100}',
                hoverBackground: '{surface.200}',
                activeBackground: '{surface.50}',
                borderColor: '{surface.100}',
                hoverBorderColor: '{surface.200}',
                activeBorderColor: '{surface.50}',
                color: '{surface.900}',
                hoverColor: '{surface.900}',
                activeColor: '{surface.900}',
                focusRing: {
                    color: '{surface.100}',
                    shadow: '0 0 0 2px color-mix(in srgb, {surface.100}, transparent 70%)',
                },
            },
        },
        outlined: {
            primary: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                borderColor: '{primary.500}',
                color: '{primary.400}',
            },
            secondary: {
                hoverBackground: 'color-mix(in srgb, {surface.400}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.400}, transparent 80%)',
                borderColor: '{surface.600}',
                color: '{surface.300}',
            },
            success: {
                hoverBackground: 'color-mix(in srgb, {green.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {green.500}, transparent 80%)',
                borderColor: '{green.500}',
                color: '{green.400}',
            },
            info: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                borderColor: '{primary.500}',
                color: '{primary.400}',
            },
            warn: {
                hoverBackground: 'color-mix(in srgb, {yellow.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {yellow.500}, transparent 80%)',
                borderColor: '{yellow.500}',
                color: '{yellow.400}',
            },
            help: {
                hoverBackground: 'color-mix(in srgb, {purple.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {purple.500}, transparent 80%)',
                borderColor: '{purple.500}',
                color: '{purple.400}',
            },
            danger: {
                hoverBackground: 'color-mix(in srgb, {red.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {red.500}, transparent 80%)',
                borderColor: '{red.500}',
                color: '{red.400}',
            },
            contrast: {
                hoverBackground: 'color-mix(in srgb, {surface.100}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.100}, transparent 80%)',
                borderColor: '{surface.100}',
                color: '{surface.200}',
            },
            plain: {
                hoverBackground: '{surface.800}',
                activeBackground: '{surface.700}',
                borderColor: '{surface.600}',
                color: '{surface.300}',
            },
        },
        text: {
            primary: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                color: '{primary.400}',
            },
            secondary: {
                hoverBackground: 'color-mix(in srgb, {surface.400}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.400}, transparent 80%)',
                color: '{surface.300}',
            },
            success: {
                hoverBackground: 'color-mix(in srgb, {green.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {green.500}, transparent 80%)',
                color: '{green.400}',
            },
            info: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                color: '{primary.400}',
            },
            warn: {
                hoverBackground: 'color-mix(in srgb, {yellow.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {yellow.500}, transparent 80%)',
                color: '{yellow.400}',
            },
            help: {
                hoverBackground: 'color-mix(in srgb, {purple.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {purple.500}, transparent 80%)',
                color: '{purple.400}',
            },
            danger: {
                hoverBackground: 'color-mix(in srgb, {red.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {red.500}, transparent 80%)',
                color: '{red.400}',
            },
            contrast: {
                hoverBackground: 'color-mix(in srgb, {surface.100}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.100}, transparent 80%)',
                color: '{surface.200}',
            },
            plain: {
                hoverBackground: '{surface.800}',
                activeBackground: '{surface.700}',
                color: '{surface.300}',
            },
        },
        link: {
            color: '{primary.400}',
            hoverColor: '{primary.300}',
            activeColor: '{primary.500}',
        },
    },
};

export default {
    root,
    colorScheme,
} satisfies ButtonDesignTokens;
